/**
 * @file The single confidence/provenance Tag shared by every Mapping Review
 * surface (rows, columns, fields, reference resolutions). It encodes the one
 * rule the panel must never break: a human override is *provenance*, not a
 * reason to hide the model's uncertainty. So a `manual` mapping renders a blue
 * "you chose this" badge that still carries the AI's score for the chosen
 * target when we have it (`manual · AI 44%`), falling back to a plain `manual`
 * badge when no per-pick score is plumbed — it never swaps a real score for a
 * flat badge the way the old per-surface tags did. An untouched AI proposal
 * renders the band-coloured
 * percent (green ≥ high, orange ≥ warn, red below) with a warn icon below
 * threshold. Callers that tint a row background read the same band by calling
 * `mappingReviewConfidence(manual ? aiScore : confidence)`.
 */
import { FC } from 'react';
import { Tag, Tooltip } from 'antd';
import { UserOutlined, WarningOutlined } from '@ant-design/icons';
import { CONFIDENCE_HIGH_THRESHOLD, CONFIDENCE_WARN_THRESHOLD } from '#types';
import { mappingReviewConfidence } from './mappingReviewConfidence';

const RUBRIC = `Green ≥ ${Math.round(CONFIDENCE_HIGH_THRESHOLD * 100)}% · Orange ${Math.round(
  CONFIDENCE_WARN_THRESHOLD * 100,
)}–${Math.round(CONFIDENCE_HIGH_THRESHOLD * 100) - 1}% · Red < ${Math.round(CONFIDENCE_WARN_THRESHOLD * 100)}%`;

const MappingReviewConfidenceTag: FC<{
  /** True once the user re-targeted or hand-set this mapping. */
  manual: boolean;
  /** The AI's confidence for an untouched proposal. */
  confidence?: number;
  /** The AI's probability for the user's chosen target (manual mappings only). */
  aiScore?: number;
}> = ({ manual, confidence, aiScore }) => {
  if (manual) {
    const scored = aiScore !== undefined;
    return (
      <Tooltip
        title={
          scored
            ? `You chose this mapping. The AI scored the chosen target ${mappingReviewConfidence(aiScore).label}.`
            : 'You chose this mapping.'
        }
      >
        <Tag color="blue" icon={<UserOutlined />} style={{ margin: 0 }}>
          {scored ? `manual · AI ${mappingReviewConfidence(aiScore).label}` : 'manual'}
        </Tag>
      </Tooltip>
    );
  }
  if (confidence === undefined) return null;
  const view = mappingReviewConfidence(confidence);
  return (
    <Tooltip title={`AI confidence — ${RUBRIC}`}>
      <Tag color={view.color} icon={view.warn ? <WarningOutlined /> : undefined} style={{ margin: 0 }}>
        {view.label}
      </Tag>
    </Tooltip>
  );
};

export default MappingReviewConfidenceTag;
